import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle, LogIn } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import SEO from '../../components/common/SEO';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../../components/admin/EditableElement';
import { Turnstile } from '../../components/Turnstile';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.rumio.world';

// Hero section component with CMS integration
function LoginHero() {
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get content with fallback (CMS > i18n > hardcoded)
  const get = (key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('auth', key, translatedFallback) ?? translatedFallback;
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      {/* Decorative blur circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
          <LogIn className="w-4 h-4" />
          {t('login.badge', 'Welcome Back')}
        </div>

        {/* Title */}
        <EditableElement section="auth" field="login_hero_title">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
            {get('login_hero_title', 'Sign in to your', 'login.heroTitle')}{' '}
            <span className="text-accent">{get('login_hero_title_highlight', 'Account', 'login.heroTitleHighlight')}</span>
          </h1>
        </EditableElement>

        {/* Subtitle */}
        <EditableElement section="auth" field="login_hero_subtitle">
          <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto animate-slide-up animation-delay-100">
            {get('login_hero_subtitle', 'Track deals, save products, and never miss a discount', 'login.heroSubtitle')}
          </p>
        </EditableElement>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
          <path
            d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
            fill="#f9fafb"
          />
        </svg>
      </div>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle, handleAuthCallback, setUser, isAuthenticated } = useAuthStore();
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get content with fallback (CMS > i18n > hardcoded)
  const get = useCallback((key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('auth', key, translatedFallback) ?? translatedFallback;
  }, [content, t]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [isTokenLoading, setIsTokenLoading] = useState(false);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
  const [turnstileToken, setTurnstileToken] = useState('');

  // Handle admin magic link token login (legacy flow - will be updated for SuperTokens)
  useEffect(() => {
    const handleTokenLogin = async () => {
      const token = searchParams.get('token');
      if (!token || isAuthenticated || isTokenLoading) return;

      setIsTokenLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_URL}/api/admin/auth/token-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (response.ok && data.user) {
          setUser(data.user);
          // Clear the token from URL and redirect to admin
          window.history.replaceState(null, '', '/login');
          navigate('/admin');
        } else {
          setError(data.error || 'Failed to sign in with magic link');
          // Clear the invalid token from URL
          window.history.replaceState(null, '', '/login');
        }
      } catch (err) {
        console.error('Token login failed:', err);
        setError('Failed to sign in. Please try logging in manually.');
        window.history.replaceState(null, '', '/login');
      } finally {
        setIsTokenLoading(false);
      }
    };

    handleTokenLogin();
  }, [searchParams, isAuthenticated, isTokenLoading, setUser, navigate]);

  // Handle SuperTokens OAuth callback
  useEffect(() => {
    const handleOAuthCallbackEffect = async () => {
      // Check for error in query params
      const urlSearchParams = new URLSearchParams(window.location.search);
      const errorParam = urlSearchParams.get('error');
      if (errorParam) {
        const errorMessages: Record<string, string> = {
          oauth_failed: get('error_oauth_failed', 'OAuth login was cancelled or failed'),
          invalid_state: get('error_invalid_state', 'Invalid OAuth state. Please try again.'),
          token_exchange_failed: get('error_token_exchange', 'Failed to complete login. Please try again.'),
          no_email: get('error_no_email', 'No email address was provided. Please use an account with an email.'),
          oauth_callback_failed: get('error_callback_failed', 'Login failed. Please try again.'),
          facebook_not_configured: get('error_facebook_not_configured', 'Facebook login is not yet available.'),
        };
        setError(errorMessages[errorParam] || get('error_callback_failed', 'Login failed. Please try again.'));
        // Clear the error from URL
        window.history.replaceState(null, '', window.location.pathname);
        return;
      }

      // Check if this is an OAuth callback (SuperTokens uses query params)
      if (urlSearchParams.has('code') || window.location.pathname.includes('/auth/callback')) {
        setIsOAuthLoading(true);
        try {
          await handleAuthCallback();
          // Get updated isAdmin from store after callback
          const { isAdmin: newIsAdmin } = useAuthStore.getState();
          navigate(newIsAdmin ? '/admin' : '/account/checklists');
        } catch (err) {
          console.error('OAuth callback failed:', err);
          setError(get('error_token_exchange', 'Failed to complete login. Please try again.'));
        } finally {
          setIsOAuthLoading(false);
        }
      }
    };

    handleOAuthCallbackEffect();
  }, [location, handleAuthCallback, navigate, get]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password, turnstileToken);
      // Get updated isAdmin from store after login
      const { isAdmin: newIsAdmin } = useAuthStore.getState();
      navigate(newIsAdmin ? '/admin' : '/account/checklists');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsOAuthLoading(true);
    try {
      // Use SuperTokens OAuth flow
      await loginWithGoogle();
      // OAuth will redirect to Google, so we don't need to set loading to false
    } catch (err) {
      setError(err instanceof Error ? err.message : get('error_connection', 'Failed to connect to authentication service'));
      setIsOAuthLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setError('');
    setIsOAuthLoading(true);
    try {
      // Get the OAuth URL from the API
      const redirectUri = `${window.location.origin}/login`;
      const response = await fetch(`${API_URL}/api/auth/apple?redirect_uri=${encodeURIComponent(redirectUri)}`);

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Apple OAuth
        window.location.href = data.url;
      } else if (response.status === 501) {
        // Not yet implemented - show friendly message
        setError(data.error || 'Apple Sign-In coming soon!');
        setIsOAuthLoading(false);
      } else {
        setError(data.error || 'Failed to initiate Apple login');
        setIsOAuthLoading(false);
      }
    } catch {
      setError(get('error_connection', 'Failed to connect to authentication service'));
      setIsOAuthLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setError('');
    setIsOAuthLoading(true);
    try {
      const redirectUri = `${window.location.origin}/login`;
      const response = await fetch(`${API_URL}/api/auth/facebook?redirect_uri=${encodeURIComponent(redirectUri)}`);

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else if (response.status === 501) {
        setError(data.error || 'Facebook Sign-In coming soon!');
        setIsOAuthLoading(false);
      } else {
        setError(data.error || 'Failed to initiate Facebook login');
        setIsOAuthLoading(false);
      }
    } catch {
      setError(get('error_connection', 'Failed to connect to authentication service'));
      setIsOAuthLoading(false);
    }
  };

  // Show loading state while exchanging admin token
  if (isTokenLoading) {
    return (
      <>
        <SEO page="login" />
        <div className="min-h-screen bg-gray-50">
          <LoginHero />
          <div className="relative -mt-24 pb-16">
            <div className="max-w-md mx-auto px-4">
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-center animate-fade-in animation-delay-200">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
                <p className="text-gray-600">{t('login.signingIn', 'Signing you in...')}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO page="login" />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <LoginHero />

        {/* Form Card - Overlapping position */}
        <div className="relative -mt-24 pb-16">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fade-in animation-delay-200">
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error message */}
                {error && (
                  <div role="alert" className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {get('email_label', 'Email address')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      aria-describedby={error ? 'login-error' : undefined}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder={get('email_placeholder', 'you@example.com')}
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    {get('password_label', 'Password')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder={get('password_placeholder', 'Enter your password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                {/* Forgot password */}
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {get('forgot_password_link', 'Forgot your password?')}
                  </Link>
                </div>

                {/* Turnstile */}
                {turnstileSiteKey && (
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onVerify={setTurnstileToken}
                  />
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isLoading || isOAuthLoading || (!!turnstileSiteKey && !turnstileToken)}
                  className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:ring-4 focus:ring-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? get('signing_in', 'Signing in...') : get('sign_in_button', 'Sign in')}
                </button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">
                      {get('or_continue_with', 'Or continue with')}
                    </span>
                  </div>
                </div>

                {/* Social login buttons */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={isOAuthLoading || isLoading}
                    className="flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isOAuthLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                    )}
                    <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                      {get('google', 'Google')}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleAppleLogin}
                    disabled={isOAuthLoading || isLoading}
                    className="flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isOAuthLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                      </svg>
                    )}
                    <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                      {get('apple', 'Apple')}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleFacebookLogin}
                    disabled={isOAuthLoading || isLoading}
                    className="flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isOAuthLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    )}
                    <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                      {get('facebook', 'Facebook')}
                    </span>
                  </button>
                </div>

                {/* Sign up link */}
                <p className="text-center text-sm text-gray-600">
                  {get('no_account', "Don't have an account?")}{' '}
                  <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium">
                    {get('sign_up_free', 'Sign up for free')}
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
